import React from 'react';
import { motion } from 'framer-motion';
import { InstagramIcon, LinkedinIcon, ArrowUpRightIcon } from 'lucide-react';
import { SectionHeading } from './SectionHeading';
import { useLanguage } from '../contexts/LanguageContext';
import { socialPosts, socialProfiles, socialText } from '../data/social';
import { ui } from '../data/ui';
const localeMap = {
  nl: 'nl-NL',
  en: 'en-GB',
  de: 'de-DE',
  fr: 'fr-FR',
  es: 'es-ES'
} as const;
export function SocialStream() {
  const {
    language
  } = useLanguage();
  const strings = ui[language];
  const captions = socialText[language];
  const formatter = new Intl.DateTimeFormat(localeMap[language], {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });

  // Oldest first, so the most recent post sits on the right.
  const orderedPosts = [...socialPosts].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  return <section className="mx-auto max-w-6xl px-5 py-16 sm:px-8" data-id="element-70" data-mp="2fy7jcqm7cewe">
      <SectionHeading data-id="element-71" data-mp="1jo0ja50fsrk2">{strings.sections.social}</SectionHeading>

      <div className="mt-6 flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between" data-id="element-72" data-mp="1jmfqokxoniou">
        <p className="max-w-2xl text-sm leading-relaxed text-ink/70" data-id="element-73" data-mp="1eoe43q11t18m">
          {strings.portfolio.socialIntro}
        </p>
        <div className="flex flex-wrap gap-3" data-id="element-74" data-mp="1smksmfk3xovm">
          <a href={socialProfiles.instagram.url} target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-2 rounded-full border border-ink/25 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-ink transition-colors hover:bg-white" data-id="element-75" data-mp="1omtutrw5y8hl">
            <InstagramIcon className="h-4 w-4" aria-hidden="true" data-id="element-76" data-mp="19r481rj5lrbt" />
            {socialProfiles.instagram.label}
          </a>
          <a href={socialProfiles.linkedin.url} target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-2 rounded-full border border-ink/25 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-ink transition-colors hover:bg-white" data-id="element-77" data-mp="14x9u74q8c6kn">
            <LinkedinIcon className="h-4 w-4" aria-hidden="true" data-id="element-78" data-mp="2haztk66n7k8c" />
            LinkedIn
          </a>
        </div>
      </div>

      <ul className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-79" data-mp="2m3sx4qqdkmiu">
        {orderedPosts.map((post, index) => {
        const Icon = post.platform === 'instagram' ? InstagramIcon : LinkedinIcon;
        return <motion.li key={post.id} initial={{
          opacity: 0,
          y: 14
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.4,
          delay: index * 0.05,
          ease: 'easeOut'
        }} data-id="element-80" data-mp="1tfx4bx3xferc" data-mpi={index}>
              <a href={post.url} target="_blank" rel="noreferrer noopener" className="group flex h-full flex-col bg-white transition-colors hover:bg-blush-50" data-id="element-81" data-mp="2890al8c2i62r">
                <span className="block overflow-hidden" data-id="element-82" data-mp="183ep2d6olcoi">
                  <img src={post.image} alt="" aria-hidden="true" loading="lazy" className="aspect-square w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" data-id="element-83" data-mp="184ziu0l68rlb" />
                </span>
                <span className="flex flex-1 flex-col p-4" data-id="element-84" data-mp="oensukc8ho6j">
                  <span className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-ink/45" data-id="element-85" data-mp="og8n08umz53v">
                    <Icon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-86" data-mp="kff0njwudyd3" />
                    {post.platform === 'instagram' ? socialProfiles.instagram.label : socialProfiles.linkedin.label}
                  </span>
                  <span className="mt-2 text-sm leading-relaxed text-ink/80" data-id="element-87" data-mp="29r7epvpoapm5">
                    {captions[post.id]}
                  </span>
                  <span className="mt-auto flex items-center justify-between gap-3 pt-4 text-[10px] uppercase tracking-[0.2em] text-ink/45" data-id="element-88" data-mp="24r2xu4kursve">
                    <time dateTime={post.date} data-id="element-89" data-mp="16hmidmio2n9d">{formatter.format(new Date(post.date))}</time>
                    <ArrowUpRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-90" data-mp="si3c6z9f1lzp" />
                  </span>
                </span>
              </a>
            </motion.li>;
      })}
      </ul>
    </section>;
}